import { useEffect, useRef } from 'react'
import type { DebugView } from './registry'
import { debugToolsEnabled } from './debug-enabled'

type Emit<T> = (value: T) => void
type DebugSetup<T> = (emit: Emit<T>) => (() => void) | void

export type UseDebug = <T>(
  channel: string,
  key: string,
  setup: DebugSetup<T>,
  deps?: readonly unknown[],
) => void

// Dev implementation — subscribes via user-supplied setup, emits to the registry
// keyed by (channel, key). Each emit appends to a rolling history (see
// HISTORY_LIMIT in registry.ts); entries for this (channel, key) are dropped
// on teardown so unmounted panels don't leak into the sidebar.
//
// Every integration point (setup, emit, cleanup) is wrapped in try/catch so a
// buggy debug callback can never take down the host component. This is
// dev-only belt-and-suspenders — in prod the whole function is tree-shaken and
// the noop runs instead.
const useDebugDev: UseDebug = (channel, key, setup, deps = []) => {
  const setupRef = useRef(setup)
  setupRef.current = setup

  useEffect(() => {
    let disposed = false
    let cleanup: (() => void) | void
    let removeKey: (() => void) | undefined

    void import('./registry').then(({ debugRegistry }) => {
      removeKey = () => debugRegistry.removeKey(channel, key)
      if (disposed) return
      try {
        cleanup = setupRef.current((value) => {
          try {
            debugRegistry.emit(channel, key, value)
          } catch (err) {
            console.warn('[ett-debug] emit failed', channel, key, err)
          }
        })
      } catch (err) {
        console.warn('[ett-debug] setup failed', channel, key, err)
      }
    })

    return () => {
      disposed = true
      try {
        removeKey?.()
      } catch (err) {
        console.warn('[ett-debug] removeKey failed', channel, key, err)
      }
      if (typeof cleanup === 'function') {
        try {
          cleanup()
        } catch (err) {
          console.warn('[ett-debug] cleanup failed', channel, key, err)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, key, ...deps])
}

const useDebugNoop: UseDebug = () => {}

/**
 * Generic debug hook. Call from any component that wants to feed the dev-tools
 * sidebar. The `setup` closure receives an `emit` function; wire it to whatever
 * source you want (datasource callback, timer, state change, etc) and return
 * an unsubscribe function.
 *
 * In prod, `useDebug` resolves to a noop — the dev impl, the registry, and the
 * sidebar are all tree-shaken out. Verify with `grep -r debugRegistry dist/`.
 *
 * Example:
 *   useDebug('scanner', panelId, (emit) => {
 *     return datasource.onDebug(({ request, response }) => {
 *       emit({ scanName, request, response })
 *     })
 *   }, [datasource, panelId])
 */
export const useDebug: UseDebug = debugToolsEnabled ? useDebugDev : useDebugNoop

type RegisterView = <T>(channel: string, view: DebugView<T>) => () => void

const registerDebugViewDev: RegisterView = (channel, view) => {
  let disposed = false
  let unregister = () => {}
  void import('./registry').then(({ debugRegistry }) => {
    if (disposed) return
    try {
      unregister = debugRegistry.registerView(channel, view)
    } catch (err) {
      console.warn('[ett-debug] registerView failed', channel, err)
    }
  })
  return () => {
    disposed = true
    unregister()
  }
}
const registerDebugViewNoop: RegisterView = () => () => {}

/**
 * Register a custom sidebar view for a channel. The sidebar falls back to a
 * JSON dump if no view is registered. Call once at module scope or in a mount
 * effect; returns an unregister function.
 *
 * Like `useDebug`, this resolves to a noop in prod and is tree-shaken.
 */
export const registerDebugView: RegisterView = debugToolsEnabled
  ? registerDebugViewDev
  : registerDebugViewNoop
